// MuscleAnatomyDiagram — schematic front + back human silhouette with
// individual muscle-group paths. Pass `primary` and `secondary` muscle
// keys to colour them. Used by ExerciseLine to show, at-a-glance, exactly
// which muscles an exercise trains — no extra tap required.
//
// Muscle keys (must match exerciseMuscles.js):
//   FRONT: traps, front_delts, chest, biceps, forearms_front, abs, obliques,
//          quads, calves_front, hip_flexors
//   BACK:  upper_traps, rear_delts, lats, mid_back, lower_back, triceps,
//          forearms_back, glutes, hamstrings, calves_back

import { memo } from "react";

const PRIMARY_FILL   = "#dc2626"; // red-600
const SECONDARY_FILL = "#f59e0b"; // amber-500
const BASE_FILL      = "rgba(255,255,255,0.07)";
const OUTLINE        = "rgba(255,255,255,0.30)";

function fillFor(name, primary, secondary) {
  if (primary?.includes(name))   return PRIMARY_FILL;
  if (secondary?.includes(name)) return SECONDARY_FILL;
  return BASE_FILL;
}

// =============================================================================
// FRONT BODY — abstract muscular silhouette. Each <path> is one muscle group.
// All coordinates are inside a 120×260 viewBox.
// =============================================================================
function FrontBody({ primary = [], secondary = [] }) {
  return (
    <svg
      viewBox="0 0 120 260"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "auto", display: "block" }}
      aria-label="Front body muscle diagram"
    >
      {/* Silhouette outline (head + body) */}
      <g stroke={OUTLINE} strokeWidth="1.2" fill={BASE_FILL}>
        {/* head */}
        <ellipse cx="60" cy="22" rx="13" ry="16" />
        {/* neck */}
        <rect x="54" y="36" width="12" height="9" />
        {/* torso */}
        <path d="M 32 50 Q 42 45 60 45 Q 78 45 88 50 L 92 110 Q 88 134 84 150 L 76 152 L 70 152 L 60 150 L 50 152 L 44 152 L 36 150 Q 32 134 28 110 Z" />
        {/* upper arms */}
        <path d="M 28 55 L 22 95 L 26 120 L 32 118 L 36 95 L 34 55 Z" />
        <path d="M 92 55 L 98 95 L 94 120 L 88 118 L 84 95 L 86 55 Z" />
        {/* forearms */}
        <path d="M 22 95 L 18 138 L 24 148 L 30 145 L 32 118 Z" />
        <path d="M 98 95 L 102 138 L 96 148 L 90 145 L 88 118 Z" />
        {/* pelvis / hips */}
        <path d="M 36 150 L 36 168 L 60 175 L 84 168 L 84 150 Z" />
        {/* thighs */}
        <path d="M 38 170 L 32 222 L 50 226 L 56 175 Z" />
        <path d="M 82 170 L 88 222 L 70 226 L 64 175 Z" />
        {/* calves */}
        <path d="M 34 222 L 32 252 L 48 252 L 50 226 Z" />
        <path d="M 86 222 L 88 252 L 72 252 L 70 226 Z" />
      </g>

      {/* MUSCLE GROUPS (clickable-looking colored regions) */}
      <g stroke={OUTLINE} strokeWidth="0.8">
        {/* traps (front) — small slivers running from neck onto shoulders */}
        <path d="M 48 45 Q 60 42 72 45 L 76 52 Q 68 49 60 49 Q 52 49 44 52 Z"
              fill={fillFor("traps", primary, secondary)} />

        {/* front delts (anterior deltoids) */}
        <path d="M 30 52 Q 26 60 30 78 L 38 72 L 40 54 Z"
              fill={fillFor("front_delts", primary, secondary)} />
        <path d="M 90 52 Q 94 60 90 78 L 82 72 L 80 54 Z"
              fill={fillFor("front_delts", primary, secondary)} />

        {/* chest (pectorals) — left + right pec */}
        <path d="M 42 58 Q 50 56 58 58 L 59 88 Q 50 92 42 88 Z"
              fill={fillFor("chest", primary, secondary)} />
        <path d="M 78 58 Q 70 56 62 58 L 61 88 Q 70 92 78 88 Z"
              fill={fillFor("chest", primary, secondary)} />

        {/* biceps */}
        <path d="M 25 74 Q 22 92 26 110 L 32 108 L 33 76 Z"
              fill={fillFor("biceps", primary, secondary)} />
        <path d="M 95 74 Q 98 92 94 110 L 88 108 L 87 76 Z"
              fill={fillFor("biceps", primary, secondary)} />

        {/* forearms (front) */}
        <path d="M 22 110 L 20 138 L 26 144 L 30 140 L 30 112 Z"
              fill={fillFor("forearms_front", primary, secondary)} />
        <path d="M 98 110 L 100 138 L 94 144 L 90 140 L 90 112 Z"
              fill={fillFor("forearms_front", primary, secondary)} />

        {/* abs (six-pack) — 3 stacked pairs of rectangles */}
        <g fill={fillFor("abs", primary, secondary)}>
          <rect x="52" y="92"  width="7" height="9" rx="1.5" />
          <rect x="61" y="92"  width="7" height="9" rx="1.5" />
          <rect x="52" y="104" width="7" height="9" rx="1.5" />
          <rect x="61" y="104" width="7" height="9" rx="1.5" />
          <rect x="52" y="116" width="7" height="9" rx="1.5" />
          <rect x="61" y="116" width="7" height="9" rx="1.5" />
          <rect x="53" y="128" width="14" height="8" rx="2" />
        </g>

        {/* obliques — left + right side */}
        <path d="M 42 95 Q 39 115 44 138 L 51 138 L 51 94 Z"
              fill={fillFor("obliques", primary, secondary)} />
        <path d="M 78 95 Q 81 115 76 138 L 69 138 L 69 94 Z"
              fill={fillFor("obliques", primary, secondary)} />

        {/* hip flexors — small inner triangles at top of thigh */}
        <path d="M 50 145 L 60 158 L 50 158 Z"
              fill={fillFor("hip_flexors", primary, secondary)} />
        <path d="M 70 145 L 60 158 L 70 158 Z"
              fill={fillFor("hip_flexors", primary, secondary)} />

        {/* quads — front thigh, both legs */}
        <path d="M 38 172 Q 36 198 35 220 L 50 222 L 56 178 Z"
              fill={fillFor("quads", primary, secondary)} />
        <path d="M 82 172 Q 84 198 85 220 L 70 222 L 64 178 Z"
              fill={fillFor("quads", primary, secondary)} />

        {/* calves (front shins) */}
        <path d="M 36 226 L 34 250 L 46 250 L 48 228 Z"
              fill={fillFor("calves_front", primary, secondary)} />
        <path d="M 84 226 L 86 250 L 74 250 L 72 228 Z"
              fill={fillFor("calves_front", primary, secondary)} />
      </g>

      {/* Label */}
      <text x="60" y="258" textAnchor="middle"
            fill="rgba(255,255,255,0.45)" fontSize="7"
            fontFamily="ui-sans-serif, system-ui" letterSpacing="1.5">FRONT</text>
    </svg>
  );
}

// =============================================================================
// BACK BODY — abstract muscular silhouette from behind.
// =============================================================================
function BackBody({ primary = [], secondary = [] }) {
  return (
    <svg
      viewBox="0 0 120 260"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "auto", display: "block" }}
      aria-label="Back body muscle diagram"
    >
      <g stroke={OUTLINE} strokeWidth="1.2" fill={BASE_FILL}>
        <ellipse cx="60" cy="22" rx="13" ry="16" />
        <rect x="54" y="36" width="12" height="9" />
        <path d="M 32 50 Q 42 45 60 45 Q 78 45 88 50 L 92 110 Q 88 134 84 150 L 76 152 L 70 152 L 60 150 L 50 152 L 44 152 L 36 150 Q 32 134 28 110 Z" />
        <path d="M 28 55 L 22 95 L 26 120 L 32 118 L 36 95 L 34 55 Z" />
        <path d="M 92 55 L 98 95 L 94 120 L 88 118 L 84 95 L 86 55 Z" />
        <path d="M 22 95 L 18 138 L 24 148 L 30 145 L 32 118 Z" />
        <path d="M 98 95 L 102 138 L 96 148 L 90 145 L 88 118 Z" />
        <path d="M 36 150 L 36 168 L 60 175 L 84 168 L 84 150 Z" />
        <path d="M 38 170 L 32 222 L 50 226 L 56 175 Z" />
        <path d="M 82 170 L 88 222 L 70 226 L 64 175 Z" />
        <path d="M 34 222 L 32 252 L 48 252 L 50 226 Z" />
        <path d="M 86 222 L 88 252 L 72 252 L 70 226 Z" />
      </g>

      <g stroke={OUTLINE} strokeWidth="0.8">
        {/* upper traps */}
        <path d="M 44 46 Q 60 41 76 46 L 78 60 Q 60 56 42 60 Z"
              fill={fillFor("upper_traps", primary, secondary)} />

        {/* rear delts */}
        <path d="M 30 52 Q 26 62 30 78 L 38 72 L 40 54 Z"
              fill={fillFor("rear_delts", primary, secondary)} />
        <path d="M 90 52 Q 94 62 90 78 L 82 72 L 80 54 Z"
              fill={fillFor("rear_delts", primary, secondary)} />

        {/* lats — broad wings from armpit to lower-mid back */}
        <path d="M 40 62 Q 32 90 36 120 L 50 118 L 54 64 Z"
              fill={fillFor("lats", primary, secondary)} />
        <path d="M 80 62 Q 88 90 84 120 L 70 118 L 66 64 Z"
              fill={fillFor("lats", primary, secondary)} />

        {/* mid back (rhomboids + mid traps) — central vertical strip */}
        <path d="M 54 60 L 66 60 L 66 120 L 54 120 Z"
              fill={fillFor("mid_back", primary, secondary)} />

        {/* lower back (erectors) */}
        <path d="M 50 122 L 70 122 L 72 148 L 48 148 Z"
              fill={fillFor("lower_back", primary, secondary)} />

        {/* triceps — outer upper arm */}
        <path d="M 22 76 Q 19 96 24 116 L 30 114 L 30 78 Z"
              fill={fillFor("triceps", primary, secondary)} />
        <path d="M 98 76 Q 101 96 96 116 L 90 114 L 90 78 Z"
              fill={fillFor("triceps", primary, secondary)} />

        {/* forearms (back) */}
        <path d="M 22 116 L 20 142 L 26 146 L 30 142 L 30 118 Z"
              fill={fillFor("forearms_back", primary, secondary)} />
        <path d="M 98 116 L 100 142 L 94 146 L 90 142 L 90 118 Z"
              fill={fillFor("forearms_back", primary, secondary)} />

        {/* glutes — buttocks */}
        <path d="M 38 152 Q 48 148 60 152 Q 60 174 48 174 Q 38 172 36 162 Z"
              fill={fillFor("glutes", primary, secondary)} />
        <path d="M 82 152 Q 72 148 60 152 Q 60 174 72 174 Q 82 172 84 162 Z"
              fill={fillFor("glutes", primary, secondary)} />

        {/* hamstrings */}
        <path d="M 38 178 Q 36 200 36 220 L 50 222 L 56 180 Z"
              fill={fillFor("hamstrings", primary, secondary)} />
        <path d="M 82 178 Q 84 200 84 220 L 70 222 L 64 180 Z"
              fill={fillFor("hamstrings", primary, secondary)} />

        {/* calves (back) */}
        <path d="M 36 226 L 34 250 L 46 250 L 48 228 Z"
              fill={fillFor("calves_back", primary, secondary)} />
        <path d="M 84 226 L 86 250 L 74 250 L 72 228 Z"
              fill={fillFor("calves_back", primary, secondary)} />
      </g>

      <text x="60" y="258" textAnchor="middle"
            fill="rgba(255,255,255,0.45)" fontSize="7"
            fontFamily="ui-sans-serif, system-ui" letterSpacing="1.5">BACK</text>
    </svg>
  );
}

function MuscleAnatomyDiagram({ primary = [], secondary = [], compact = false, testId }) {
  // We always render BOTH front + back. The user explicitly asked for both
  // visible by default — no taps required.
  const containerStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    padding: 10,
    borderRadius: 12,
    background: "var(--surface-soft)",
    border: "1px solid var(--line)",
  };
  const sideStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: compact ? 110 : 180,
    margin: "0 auto",
  };

  // Build the "muscles worked" caption from the muscle keys.
  const allPrimary = [...new Set(primary)];
  const allSecondary = [...new Set(secondary)];

  return (
    <div data-testid={testId || "muscle-diagram"}>
      <div style={containerStyle}>
        <div style={sideStyle}>
          <FrontBody primary={allPrimary} secondary={allSecondary} />
        </div>
        <div style={sideStyle}>
          <BackBody primary={allPrimary} secondary={allSecondary} />
        </div>
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex", gap: 14, marginTop: 8,
          fontSize: 10, color: "var(--text-faint)",
          letterSpacing: "0.10em", textTransform: "uppercase",
          fontWeight: 600,
          justifyContent: "center",
        }}
        data-testid="muscle-diagram-legend"
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: PRIMARY_FILL, display: "inline-block" }} />
          Primary
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: SECONDARY_FILL, display: "inline-block" }} />
          Secondary
        </span>
      </div>
    </div>
  );
}

export default memo(MuscleAnatomyDiagram);
